import { type SanitizedResult } from '@amzn/bf-gql'
import { getPlaybackEnv } from '../env/playbackEnvRef'
import { gql } from '../gql/gql'
import { podcastEpisodeFieldsFrag } from '../gql/podcastEpisodeFieldsFrag'
import { trackFieldsFrag } from '../gql/trackFieldsFrag'
import { invalidateQueueViewCache } from './queueViewCache'

/**
 * Cloud-queue view GraphQL — read the paginated queue view (current → upcoming)
 * and remove / reorder entries. Shared by any surface that renders a cloud queue
 * (the now-playing queue list, agent-schema getters); it is not party-specific.
 * Party-only wire (the Hermes `queueUpdated` subscription) lives with the app's
 * party code.
 */

// language=GraphQL
//
// `view` is a paginated connection anchored on the current entry. `direction`
// (a Firefly-only arg absent from Green Hornet — FORWARD | BACKWARD, default
// FORWARD) chooses which side of the anchor it walks: FORWARD returns the
// current entry and everything after it (upcoming); BACKWARD returns the entries
// before it (already-played), oldest-first. BACKWARD vends no usable token: page
// older by passing the oldest held `entityReferenceId` as the `cursor`.
// It caps at a small server-default page when `limit` is omitted, so a long
// queue truncates — page forward by feeding `pageInfo.token` back as the next
// `cursor` until `hasNextPage` is false.
const queueViewQuery = gql(
    `
  query QueueView($id: String!, $currentEntityReferenceId: String, $isAuthenticated: Boolean!, $limit: Float, $cursor: String, $direction: String) {
    queue(id: $id, currentEntityReferenceId: $currentEntityReferenceId) {
      id
      currentEntityReferenceId
      view(limit: $limit, cursor: $cursor, direction: $direction) {
        pageInfo {
          hasNextPage
          token
        }
        edges {
          entityReferenceId
          metricId
          node {
            ... on Track {
              ...trackFields
            }
            ... on PodcastEpisode {
              ...podcastEpisodeFields
            }
          }
        }
      }
    }
  }
`,
    [trackFieldsFrag, podcastEpisodeFieldsFrag]
)

/** Cloud-queue view page size — first page and every cursor-driven fetch-more. */
export const QUEUE_VIEW_PAGE_SIZE = 20

// language=GraphQL
const removeFromQueueMutation = gql(`
  mutation RemoveFromQueue($id: String!, $entityReferenceIds: [String!]) {
    removeFromQueue(id: $id, entityReferenceIds: $entityReferenceIds) {
      status
    }
  }
`)

export async function removeFromCloudQueue(
    queueId: string,
    entityReferenceIds: string[]
): Promise<void> {
    await getPlaybackEnv().gql.fetch(
        removeFromQueueMutation,
        { id: queueId, entityReferenceIds },
        { isRetryableError: () => false }
    )
    invalidateQueueViewCache()
}

// language=GraphQL
//
// Reorder a single entry in a cloud queue by positioning it relative
// to a sibling. Mirrors the playlist `moveTracks` shape but talks to
// the queue backend — `placement: 'AFTER'` seats the moved entry
// immediately after `placementEntityReferenceId`, `BEFORE` seats it
// immediately before.
const reorderQueueMutation = gql(`
  mutation ReorderQueue(
    $id: String!
    $entityReferenceId: String!
    $placementEntityReferenceId: String!
    $placement: String!
  ) {
    reorderQueue(
      id: $id
      entityReferenceId: $entityReferenceId
      placementEntityReferenceId: $placementEntityReferenceId
      placement: $placement
    ) {
      status
    }
  }
`)

export interface ReorderQueueInput {
    readonly queueId: string
    readonly entityReferenceId: string
    readonly placementEntityReferenceId: string
    readonly placement: 'BEFORE' | 'AFTER'
}

export async function reorderCloudQueue(input: ReorderQueueInput): Promise<void> {
    await getPlaybackEnv().gql.fetch(
        reorderQueueMutation,
        {
            id: input.queueId,
            entityReferenceId: input.entityReferenceId,
            placementEntityReferenceId: input.placementEntityReferenceId,
            placement: input.placement,
        },
        { isRetryableError: () => false }
    )
    invalidateQueueViewCache()
}

export type QueueViewResult = SanitizedResult<typeof queueViewQuery>

/** Which side of the current anchor the `view` connection walks. */
export type QueueViewDirection = 'FORWARD' | 'BACKWARD'

export interface QueueViewPageOptions {
    /** Page size. Defaults to {@link QUEUE_VIEW_PAGE_SIZE}. */
    readonly limit?: number
    /** `pageInfo.token` from the prior page; null/omitted reads the first page. */
    readonly cursor?: string | null
    /** Walk direction. Omitted → the server default (FORWARD / upcoming). */
    readonly direction?: QueueViewDirection
}

export async function fetchQueueView(
    id: string,
    currentEntityReferenceId: string | null,
    options?: QueueViewPageOptions
): Promise<QueueViewResult> {
    const { auth, gql: client } = getPlaybackEnv()
    return client.fetch(
        queueViewQuery,
        {
            id,
            currentEntityReferenceId,
            isAuthenticated: auth.isSignedIn(),
            limit: options?.limit ?? QUEUE_VIEW_PAGE_SIZE,
            cursor: options?.cursor ?? null,
            direction: options?.direction ?? null,
        },
        { isRetryableError: () => true }
    )
}

/**
 * Read the already-played side of the queue via the same `view` connection with
 * `direction: BACKWARD` — anchored on the current entry, returning the entries
 * before it oldest-first. Replaces the `previous` entity
 * connection for the queue-view history strip; unlike `previous`, `view` has no
 * 1..10 limit ceiling, so a full page is safe.
 */
export async function fetchPreviousQueueView(
    id: string,
    currentEntityReferenceId: string | null,
    limit: number = QUEUE_VIEW_PAGE_SIZE
): Promise<QueueViewResult> {
    return fetchQueueView(id, currentEntityReferenceId, { limit, direction: 'BACKWARD' })
}
